import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import { useNavigate } from 'react-router-dom';
import Loader from '../../../../component/common/Loader';
import { formatBigNumber, lastNDaysLabels } from '../../../SleepHub/sleepHubApi';
import { getLocalStorageItem } from '../../../../utils/helper';
import { fetchCompanySelfDevelopmentOverview } from './companySelfDevelopmentApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  ProgressList,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID
} from '../../../../component/common/designSystem';
import { TOOL_COLORS, BREADTH_RAMP, toolColor } from './selfDevelopmentTheme';

/** Where each tool's own page lives, for the tool-by-tool table's links. */
const TOOL_PATHS = {
  gratitude: 'gratitude',
  cleanse: 'cleanse',
  journal: 'journal',
  goals: 'goals',
  vision_board: 'vision-board',
  affirmations: 'affirmations'
};

/**
 * Self Development → Overview.
 *
 * Three things carry the page:
 *
 *  1. HOW MANY PEOPLE PRACTISE ANYTHING. Six separate take-up numbers answer
 *     six small questions; the number a company actually wants is how many of
 *     its people do any of this at all. It is counted across all six tools at
 *     once, so somebody who journals on Monday and sets a goal on Tuesday is
 *     one person practising, not two features being used.
 *  2. BREADTH. One tool is somebody who tried the app; four is somebody with a
 *     practice. Tools-per-person is the only distribution here that genuinely
 *     reads better as it climbs, and it is the one to move.
 *  3. FOLLOW-THROUGH, TOOL BY TOOL. Every tool asks one thing beyond writing
 *     the entry — save it, finish it, achieve it, put something on the board —
 *     so the table carries that alongside volume. A tool with high volume and
 *     low follow-through is a tool people open and abandon, and it looks
 *     identical to a healthy one if you only count entries.
 *
 * What anybody wrote is not here and not reachable from here. Nor is the stored
 * sentiment read on it: mood check-ins are the self-reported wellbeing signal
 * and have their own section.
 *
 * `fetcher` is a prop so an unscoped hub could render this page unchanged.
 */
const SelfDevelopmentOverview = ({
  fetcher = fetchCompanySelfDevelopmentOverview,
  populationLabel = 'users'
}) => {
  const navigate = useNavigate();
  const userData = JSON.parse(getLocalStorageItem('userData') || '{}');
  const slug = userData?.slug;

  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  const toolBreakdown = stats?.toolBreakdown || [];
  const toolsPerUser = stats?.toolsPerUser || [];
  const consistencySplit = stats?.consistencySplit || [];
  const entriesTrend = stats?.entriesTrend || [];
  const peopleTrend = stats?.peopleTrend || [];

  const trendHasData = entriesTrend.some((value) => value > 0);

  // Entry mix keeps TOOL_ORDER (the backend's order) rather than ranking, so a
  // tool sits in the same slice position on every page of the section.
  const toolMix = toolBreakdown.map((row) => ({ label: row.label, count: row.entries }));
  const toolMixColors = toolBreakdown.map((row) => toolColor(row.key));

  const busiestTool = toolBreakdown.reduce(
    (best, row) => (!best || row.entries > best.entries ? row : best),
    null
  );

  const trendOptions = {
    chart: { ...CHART_BASE, type: 'line', stacked: false },
    colors: [CHART_COLORS.brand, CHART_COLORS.teal],
    stroke: { width: [0, 3], curve: 'smooth' },
    plotOptions: { bar: { columnWidth: '65%', borderRadius: 2 } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: lastNDaysLabels(entriesTrend.length), tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { shared: true }
  };

  /**
   * Take-up per tool as one horizontal bar chart. Every bar is a share of the
   * SAME denominator — the whole population — so the tools are directly
   * comparable, which a per-tool donut could never be.
   */
  const adoptionOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [({ dataPointIndex }) => toolColor(toolBreakdown[dataPointIndex]?.key)],
    plotOptions: {
      bar: { horizontal: true, borderRadius: 3, barHeight: '55%', distributed: true }
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    grid: CHART_GRID,
    xaxis: {
      categories: toolBreakdown.map((row) => row.label),
      labels: { formatter: (val) => `${Math.round(val)}%` },
      max: 100
    },
    tooltip: {
      y: {
        formatter: (val, { dataPointIndex }) =>
          `${val}% — ${formatBigNumber(toolBreakdown[dataPointIndex]?.users || 0)} people`
      }
    }
  };

  return (
    <div>
      {loader ? <Loader /> : null}

      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.users}
          title="People Practising"
          value={stats ? formatBigNumber(stats.activeUsers) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.activeUsers}
          title={`Share of ${populationLabel}`}
          value={stats?.totalUsers > 0 ? `${stats.adoption}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.logs}
          title="Entries Written"
          value={stats ? formatBigNumber(stats.totalEntries) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.custom}
          title="Entries per Person"
          value={stats ? stats.avgEntriesPerActiveUser : '-'}
        />
        {/* The depth number: more than one tool means a practice, not a try. */}
        <StatCard
          icon={STAT_ICONS.streak}
          title="Using 2+ Tools"
          value={stats ? formatBigNumber(stats.multiToolUsers) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Busiest Tool"
          value={busiestTool?.entries > 0 ? busiestTool.label : '-'}
        />
      </div>

      {/* ---------- Shape of the practice ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <MiniDonutCard
          title="Where the Practice Goes"
          subtitle="Entries by tool"
          data={toolMix}
          colors={toolMixColors}
          unit="entries"
          totalLabel="Entries"
          legendLimit={6}
        />
        <MiniDonutCard
          title="Tools per Person"
          subtitle="How broad each practice is, out of six"
          data={toolsPerUser}
          colors={BREADTH_RAMP}
          unit="people"
          totalLabel="People"
          legendLimit={5}
        />
        <Panel title="Days Practised" subtitle="Distinct days, across every tool">
          {/* Distinct DAYS, not entries: ten entries in one sitting is one day
              of practice, and this is the only figure on the page that says
              whether the habit stuck. */}
          <ProgressList items={consistencySplit} unit="people" color={CHART_COLORS.teal} />
        </Panel>
      </div>

      {/* ---------- Trend and take-up ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="Practice Over Time" subtitle="Entries and people — last 30 days">
          {trendHasData ? (
            <Chart
              type="line"
              height={380}
              width="100%"
              series={[
                { name: 'Entries', type: 'column', data: entriesTrend },
                { name: 'People', type: 'line', data: peopleTrend }
              ]}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>Nothing written in the last 30 days.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Take-up by Tool" subtitle={`Share of your ${populationLabel} using each one`}>
          {toolBreakdown.length > 0 && stats?.totalUsers > 0 ? (
            <Chart
              type="bar"
              height={380}
              width="100%"
              series={[{ name: 'Take-up', data: toolBreakdown.map((row) => row.adoption) }]}
              options={adoptionOptions}
            />
          ) : (
            <PanelEmpty>No self-development activity yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Tool by tool ---------- */}
      <div className="mt-6">
        <Panel
          title="Tool by Tool"
          subtitle="Reach, volume and what people do once the entry exists"
        >
          {toolBreakdown.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Tool</th>
                  <th className="py-2 px-4 font-medium text-center">People</th>
                  <th className="py-2 px-4 font-medium text-center">Take-up</th>
                  <th className="py-2 px-4 font-medium text-center">Entries</th>
                  <th className="py-2 px-4 font-medium text-center">Share</th>
                  <th className="py-2 font-medium text-right">Follow-through</th>
                </>
              }
            >
              {toolBreakdown.map((row) => (
                <tr
                  key={row.key}
                  className={`${PANEL_ROW_CLASS} cursor-pointer`}
                  onClick={() => navigate(`/${slug}/self-development/${TOOL_PATHS[row.key]}`)}
                >
                  <td className="py-2 pr-4">
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: TOOL_COLORS[row.key] }}
                      />
                      {row.label}
                    </span>
                  </td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.users)}</td>
                  <td className="py-2 px-4 text-center">{row.adoption}%</td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.entries)}</td>
                  <td className="py-2 px-4 text-center">{row.share}%</td>
                  {/* Each tool's own ask — saved, completed, achieved, filled
                      in — named so the column isn't six different things
                      pretending to be one. */}
                  <td className="py-2 text-right">
                    {row.entries > 0 ? (
                      <>
                        {row.outcomeRate}%
                        <span className="ml-1.5 text-gray-400 font-normal">
                          {row.outcomeLabel.toLowerCase()}
                        </span>
                      </>
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No self-development activity yet.</PanelEmpty>
          )}
        </Panel>
      </div>
    </div>
  );
};

SelfDevelopmentOverview.propTypes = {
  fetcher: PropTypes.func,
  populationLabel: PropTypes.string
};

export default SelfDevelopmentOverview;
